import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { contactsApi, Contact } from '../api/contacts';
import { accountsApi } from '../api/accounts';
import { tagsApi } from '../api/tags';
import PremiumLayout from '../components/PremiumLayout';
import TagSelector from '../components/TagSelector';
import TagDisplay from '../components/TagDisplay';
import CustomFieldsRenderer from '../components/CustomFieldsRenderer';
import { customFieldsApi } from '../api/customFields';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  MagnifyingGlassIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  BuildingOfficeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

type SortField = 'name' | 'email' | 'account' | 'title';
type SortDirection = 'asc' | 'desc';

const ITEMS_PER_PAGE = 20;

const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    title: '',
    account_id: '',
  });
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: contactsApi.getAll,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
  });

  const createMutation = useMutation({
    mutationFn: contactsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setShowModal(false);
      setFormError(null);
      resetForm();
    },
    onError: (err: any) => {
      const message = err?.response?.data?.error || err?.message || 'Failed to create contact';
      setFormError(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Contact> }) =>
      contactsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setShowModal(false);
      setEditingContact(null);
      setFormError(null);
      resetForm();
    },
    onError: (err: any) => {
      const message = err?.response?.data?.error || err?.message || 'Failed to update contact';
      setFormError(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: contactsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      title: '',
      account_id: '',
    });
    setSelectedTagIds([]);
    setCustomFieldValues({});
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const contactData = {
      ...formData,
      account_id: formData.account_id ? parseInt(formData.account_id) : undefined,
    };

    if (editingContact) {
      updateMutation.mutate(
        { id: editingContact.id, data: contactData },
        {
          onSuccess: async () => {
            try {
              await tagsApi.setTags('contact', editingContact.id, selectedTagIds);
              queryClient.invalidateQueries({ queryKey: ['tags', 'contact', editingContact.id] });
            } catch (error) {
              console.error('Error updating tags:', error);
            }
            try {
              if (Object.keys(customFieldValues).length > 0) {
                await customFieldsApi.setValues('contact', editingContact.id, customFieldValues);
              }
            } catch (error) {
              console.error('Error updating custom fields:', error);
            }
          },
        }
      );
    } else {
      createMutation.mutate(contactData, {
        onSuccess: async (newContact) => {
          if (selectedTagIds.length > 0) {
            try {
              await tagsApi.setTags('contact', newContact.id, selectedTagIds);
              queryClient.invalidateQueries({ queryKey: ['tags', 'contact', newContact.id] });
            } catch (error) {
              console.error('Error setting tags:', error);
            }
          }
          try {
            if (Object.keys(customFieldValues).length > 0) {
              await customFieldsApi.setValues('contact', newContact.id, customFieldValues);
            }
          } catch (error) {
            console.error('Error setting custom fields:', error);
          }
        },
      });
    }
  };

  const handleEdit = async (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email || '',
      phone: contact.phone || '',
      title: contact.title || '',
      account_id: contact.account_id?.toString() || '',
    });
    // Load tags for this contact
    try {
      const tags = await tagsApi.getByEntity('contact', contact.id);
      setSelectedTagIds(tags.map(t => t.id));
    } catch (error) {
      console.error('Error loading tags:', error);
      setSelectedTagIds([]);
    }
    // Load custom field values
    try {
      const customValues = await customFieldsApi.getValues('contact', contact.id);
      const values: Record<string, any> = {};
      Object.keys(customValues).forEach(key => {
        values[key] = customValues[key]?.value || customValues[key];
      });
      setCustomFieldValues(values);
    } catch (error) {
      console.error('Error loading custom fields:', error);
      setCustomFieldValues({});
    }
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this contact? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const getAccountName = (accountId?: number) => {
    if (!accountId) return null;
    const account = accounts.find(a => a.id === accountId);
    return account?.name;
  };

  // Filter and sort contacts
  const { filteredAndSortedContacts, totalPages } = useMemo(() => {
    // Filter
    let filtered = contacts.filter(contact =>
      `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phone?.includes(searchTerm) ||
      contact.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getAccountName(contact.account_id)?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort
    filtered.sort((a, b) => {
      let aValue: string | null = '';
      let bValue: string | null = '';

      switch (sortField) {
        case 'name':
          aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
          bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
          break;
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        case 'account':
          aValue = getAccountName(a.account_id)?.toLowerCase() || '';
          bValue = getAccountName(b.account_id)?.toLowerCase() || '';
          break;
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Paginate
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginated = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return { filteredAndSortedContacts: paginated, totalPages };
  }, [contacts, searchTerm, sortField, sortDirection, currentPage, accounts]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-4 w-4 ml-1" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 ml-1" />
    );
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Contacts">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-slate-800"></div>
        </div>
      </PremiumLayout>
    );
  }

  const totalContacts = contacts.length;
  const showingFrom = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const showingTo = Math.min(currentPage * ITEMS_PER_PAGE, filteredAndSortedContacts.length + (currentPage - 1) * ITEMS_PER_PAGE);

  return (
    <PremiumLayout
      title="Contacts"
      subtitle={`${totalContacts} total contacts • Showing ${showingFrom}-${showingTo} of ${contacts.filter(c => 
        `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone?.includes(searchTerm)
      ).length}`}
      actions={
        <button
          onClick={() => {
            resetForm();
            setEditingContact(null);
            setShowModal(true);
          }}
          className="btn-primary text-sm px-4 py-2"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Contact
        </button>
      }
    >
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="premium-card p-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search contacts by name, email, phone, title, or account..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
              className="input-premium pl-10"
            />
          </div>
        </div>

        {/* Contacts Table */}
        {filteredAndSortedContacts.length > 0 ? (
          <div className="premium-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/80 backdrop-blur-sm">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100/50 transition-colors"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Contact
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100/50 transition-colors"
                      onClick={() => handleSort('title')}
                    >
                      <div className="flex items-center">
                        Title
                        <SortIcon field="title" />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100/50 transition-colors"
                      onClick={() => handleSort('account')}
                    >
                      <div className="flex items-center">
                        Account
                        <SortIcon field="account" />
                      </div>
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100/50 transition-colors"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center">
                        Email
                        <SortIcon field="email" />
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Phone
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredAndSortedContacts.map((contact) => {
                    const initials = `${contact.first_name.charAt(0)}${contact.last_name.charAt(0)}`.toUpperCase();
                    return (
                      <tr key={contact.id} className="hover:bg-gray-50/50 transition-colors group">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0 mr-3">
                              <span className="text-white font-semibold text-sm">{initials}</span>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-900">
                                {contact.first_name} {contact.last_name}
                              </div>
                              <TagDisplay entityType="contact" entityId={contact.id} className="mt-1" />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">{contact.title || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contact.account_id && getAccountName(contact.account_id) ? (
                            <button
                              onClick={() => navigate(`/accounts/${contact.account_id}`)}
                              className="flex items-center text-sm text-gray-600 hover:text-indigo-600 transition-colors"
                            >
                              <BuildingOfficeIcon className="h-4 w-4 mr-1.5 text-gray-400" />
                              {getAccountName(contact.account_id)}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contact.email ? (
                            <a
                              href={`mailto:${contact.email}`}
                              className="flex items-center text-sm text-slate-700 hover:text-slate-900 hover:underline"
                            >
                              <EnvelopeIcon className="h-4 w-4 mr-1.5 text-gray-400" />
                              {contact.email}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {contact.phone ? (
                            <a
                              href={`tel:${contact.phone}`}
                              className="flex items-center text-sm text-slate-700 hover:text-slate-900 hover:underline"
                            >
                              <PhoneIcon className="h-4 w-4 mr-1.5 text-gray-400" />
                              {contact.phone}
                            </a>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleEdit(contact)}
                              className="p-2 text-gray-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(contact.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50/50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages}</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="premium-card p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <UserCircleIcon className="h-10 w-10 text-slate-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {searchTerm ? 'No contacts found' : 'No Contacts Yet'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Start building your contact database by adding your first contact.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => {
                    resetForm();
                    setEditingContact(null);
                    setShowModal(true);
                  }}
                  className="btn-primary"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Your First Contact
                </button>
              )}
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 pb-4 px-4">
            <div className="relative bg-white/95 backdrop-blur-md rounded-xl shadow-2xl max-w-2xl w-full border border-white/20 fade-in">
              <div className="p-6">
                <div className="mb-5">
                  <h3 className="text-xl font-bold text-gray-900">
                    {editingContact ? 'Edit Contact' : 'New Contact'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingContact ? 'Update contact information' : 'Add a new contact to your database'}
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="input-premium"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="input-premium"
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-premium"
                      placeholder="john.doe@example.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-premium"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Title
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="input-premium"
                        placeholder="CEO, Manager, etc."
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Account (Optional)
                    </label>
                    <select
                      value={formData.account_id}
                      onChange={(e) => setFormData({ ...formData, account_id: e.target.value })}
                      className="input-premium"
                    >
                      <option value="">Select an account...</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <TagSelector
                    selectedTagIds={selectedTagIds}
                    onChange={setSelectedTagIds}
                    entityType="contact"
                    entityId={editingContact?.id}
                  />
                  <CustomFieldsRenderer
                    entityType="contact"
                    entityId={editingContact?.id}
                    values={customFieldValues}
                    onChange={(fieldName, value) => {
                      setCustomFieldValues(prev => ({
                        ...prev,
                        [fieldName]: value,
                      }));
                    }}
                  />
                  {formError && (
                    <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                      {formError}
                    </div>
                  )}
                  <div className="flex space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? 'Saving...'
                        : editingContact
                        ? 'Update Contact'
                        : 'Create Contact'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingContact(null);
                        resetForm();
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default Contacts;
